import { store, useAppDispatch } from 'modules/Editor/store';
import { updateWidget } from 'modules/Editor/store/infographSlice';
import { DropdownPopover } from 'modules/common/components/ToolbarPopover';
import { useTranslation } from 'react-i18next';
import { IconWidgetData } from 'widgets/IconWidget/IconWidget.types';
import { ProgressChartWidgetData } from 'widgets/ProgressChartWidget/ProgressChartWidget.types';
import { LabelTextWidgetData } from 'widgets/TextBasedWidgets/LabelTextWidget/LabelTextWidget.types';
import { useWidget } from 'widgets/sdk';

import { swapChartType } from '../../StatChartWidget.helpers';
import { ComponentWidgetIdKeys, StatChartType, StatChartWidgetData } from '../../StatChartWidget.types';
import { CHART_SWAP_OPTIONS, CHART_SWAP_OPTIONS_NO_LABEL } from './ChartSwap.config';

export const ChartSwap = () => {
  const { type, widgetId, componentWidgetIdMap, options = { showLabel: true } } = useWidget<StatChartWidgetData>();
  const { t } = useTranslation('editor_widget_toolbar', { keyPrefix: 'statChartToolbar', useSuspense: false });
  const { t: tChart } = useTranslation('editor_widget_menu', {
    useSuspense: false,
    keyPrefix: 'sideMenu.chartsMenu',
  });
  const label = t('chartSwap.label');

  const list = (options.showLabel ? CHART_SWAP_OPTIONS : CHART_SWAP_OPTIONS_NO_LABEL).map((opt) => ({
    ...opt,
    label: tChart(`statChartSwap.${opt.label}` as keyof typeof tChart),
  }));

  const metricTextWidgetId = componentWidgetIdMap[ComponentWidgetIdKeys.MetricText];
  const chartWidgetId = componentWidgetIdMap[ComponentWidgetIdKeys.Chart];
  const iconGridWidgetId = componentWidgetIdMap[ComponentWidgetIdKeys.IconGrid];

  const dispatch = useAppDispatch();

  const selectedIndex = list.findIndex((opt) => opt.value === type) || 0;

  // There's a couple of older templates where the stat chart is missing the icon grid widget - hide the swap option for them
  const filteredSwapOptions = iconGridWidgetId ? list : list.filter((opt) => opt.value !== StatChartType.Icon);

  const onUpdateType = (newType: StatChartType) => {
    // No update if trying to swap to the same type
    if (newType === type) return;

    // access the widget data when the function is called
    // to avoid subscribing to all the stat chart widget data in the component itself
    const state = store.getState();
    const chartWidgetData = state.infograph.widgets[chartWidgetId];
    const iconWidgetData = state.infograph.widgets[iconGridWidgetId];

    const { newChartWidgetData, newMetricTextWidgetData, newIconWidgetData, newWidgetData } = swapChartType(
      type,
      newType,
      chartWidgetData as ProgressChartWidgetData,
      iconWidgetData as IconWidgetData,
      {
        showLabel: options?.showLabel,
      } as LabelTextWidgetData,
    );
    dispatch(
      updateWidget([
        { widgetId, widgetData: newWidgetData },
        { widgetId: chartWidgetId, widgetData: newChartWidgetData },
        { widgetId: metricTextWidgetId, widgetData: newMetricTextWidgetData },
        { widgetId: iconGridWidgetId, widgetData: newIconWidgetData },
      ]),
    );
  };

  return (
    <DropdownPopover
      options={filteredSwapOptions}
      selectedIndex={selectedIndex}
      label={label}
      w={'180px'}
      buttonVariant={'toolbar-dropdown-option'}
      optionsButtonVariant={'toolbar-dropdown-item'}
      onSelect={onUpdateType}
      triggerProps={{
        height: 8,
        'data-testid': 'stat-chart-swap-trigger',
      }}
      textButtonProps={{
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
      optionProps={{
        whiteSpace: 'normal',
        textAlign: 'left',
      }}
      buttonOptionProps={{
        height: '100%',
        padding: 1,
        justifyContent: 'flex-start',
      }}
      returnFocusOnClose={false}
    />
  );
};
